type ParamType = string | number

type ExtractRouteParams<T extends string> =
  T extends `${string}:${infer Param}/${infer Rest}`
    ? Param | ExtractRouteParams<Rest>
    : T extends `${string}:${infer Param}`
      ? Param
      : never

export type RouteParams<T extends string> = {
  [K in ExtractRouteParams<T>]: ParamType
}

type HasParams<T extends string> = ExtractRouteParams<T> extends never ? false : true
export type QueryParams = Record<string, ParamType>

export class Router {
  static getRoute<
    Path extends string,
    HasParameters extends boolean = HasParams<Path>,
  >(
    path: Path,
    ...args: HasParameters extends true
      ? [params: RouteParams<Path>, queryParams?: Record<string, string | number>]
      : [params?: undefined, queryParams?: Record<string, string | number>]
  ): string {
    const [params, queryParams] = args

    let route = path as string

    if (params) {
      route = Object.entries(params as Record<string, string>).reduce<string>(
        (route, [key, value]) => route.replace(`:${key}`, value),
        route,
      )
    }

    if (queryParams && Object.keys(queryParams).length > 0) {
      const searchParams = new URLSearchParams()
      Object.entries(queryParams).forEach(([key, value]) => {
        searchParams.set(key, value.toString())
      })
      route += `?${searchParams.toString()}`
    }

    return route
  }

  static buildRoute(
    ...args:
      | [...paths: string[]]
      | [...paths: string[], queryParams: QueryParams | undefined]
  ): string {
    const maybeQueryParams = args.at(-1)

    const hasQueryParams = typeof maybeQueryParams === 'object' &&
      maybeQueryParams !== null &&
      !Array.isArray(maybeQueryParams)

    const paths = hasQueryParams
      ? (args.slice(0, -1) as string[])
      : (args as string[])

    let route = paths.join('/')

    if (hasQueryParams && Object.keys(maybeQueryParams).length > 0) {
      const searchParams = new URLSearchParams()

      Object.entries(maybeQueryParams).forEach(([key, value]) => {
        searchParams.set(key, value.toString())
      })

      route += `?${searchParams.toString()}`
    }

    return route
  }
}
